import { ATTRS, TAGS } from '../constants';
import { EntitiesManager } from '../entitiesManager';
import { geoJSON2Polygons, polygons2GeoJSON } from '../geometry';
import { Entity, ISOFileInformation } from '../types';
import { CropTypeEntity } from './CropType';
import { CropVarietyEntity } from './CropVariety';
import { FarmEntity } from './Farm';
import { GuidanceGroupEntity } from './GuidanceGroup';

export class PartfieldEntity implements Entity {
  public static fromXML(
    xmlField: any,
    matchIDs: any,
    files: ISOFileInformation[],
    entitiesManager: EntitiesManager
  ) {
    const attrs = xmlField._attributes;
    const isoId = attrs[ATTRS.Partfield.PartfieldId];
    const id = matchIDs[isoId] ? matchIDs[isoId].id : null;

    const farmEntity = entitiesManager.getEntityByIsoId<FarmEntity>(
      attrs[ATTRS.Partfield.FarmIdRef]
    );
    const cropTypeEntity = entitiesManager.getEntityByIsoId<CropTypeEntity>(
      attrs[ATTRS.Partfield.CropTypeIdRef]
    );
    const cropVarietyEntity = entitiesManager.getEntityByIsoId<CropVarietyEntity>(
      attrs[ATTRS.Partfield.CropVarietyIdRef]
    );

    const guidanceGroups = (xmlField[TAGS.GuidanceGroup] || []).map(
      (xml) => GuidanceGroupEntity.fromXML(xml).entity
    );

    const entity = new PartfieldEntity(
      id,
      attrs[ATTRS.Partfield.PartfieldDesignator],
      attrs[ATTRS.Partfield.PartfieldArea],
      polygons2GeoJSON(xmlField[TAGS.Polygon]),
      farmEntity,
      cropTypeEntity,
      cropVarietyEntity,
      guidanceGroups
    );

    return { entity, isoId };
  }
  public type = TAGS.Partfield;

  constructor(
    public id: number,
    public name: string,
    public area: number,
    public boundary?: any, // GeoJSON
    public farm?: FarmEntity,
    public cropType?: CropTypeEntity,
    public cropVariety?: CropVarietyEntity,
    public guidanceGroups?: GuidanceGroupEntity[]
  ) {}

  public toXML(xmlId: string, entitiesManager: EntitiesManager) {
    const farmIsoId = entitiesManager.registerEntity(this.farm);
    const cropTypeIsoId = entitiesManager.registerEntity(this.cropType);
    const cropVarietyIsoId = entitiesManager.registerEntity(this.cropVariety);

    const guidanceGroupEntities = (this.guidanceGroups || []).map((guidanceGroup) =>
      guidanceGroup.toXML()
    );

    return {
      _attributes: {
        [ATTRS.Partfield.PartfieldId]: xmlId,
        [ATTRS.Partfield.PartfieldCode]: this.id,
        [ATTRS.Partfield.PartfieldDesignator]: this.name,
        [ATTRS.Partfield.PartfieldArea]: this.area,
        ...(farmIsoId && { [ATTRS.Partfield.FarmIdRef]: farmIsoId }),
        ...(cropTypeIsoId && { [ATTRS.Partfield.CropTypeIdRef]: cropTypeIsoId }),
        ...(cropVarietyIsoId && { [ATTRS.Partfield.CropVarietyIdRef]: cropVarietyIsoId }),
      },
      ...(this.boundary && {
        [TAGS.Polygon]: geoJSON2Polygons(this.boundary, entitiesManager.options.version),
      }),
      [TAGS.GuidanceGroup]: guidanceGroupEntities,
    };
  }
}
